<script setup>
import { computed, ref, watch } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import Button from 'dashboard/components-next/button/Button.vue';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { scheduledSnoozeDateTime } from 'dashboard/helper/snoozeHelpers';
import SnoozeBadge from 'dashboard/components-next/Conversation/ConversationCard/SnoozeBadge.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from 'dashboard/components-next/Conversation/ConversationCard/CardLabelsV5.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import UnreadBadge from 'dashboard/components-next/Conversation/ConversationCard/UnreadBadge.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  isPinned: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  actionsMenuOpen: { type: Boolean, default: false },
});

const emit = defineEmits([
  'click',
  'contextmenu',
  'selectConversation',
  'deSelectConversation',
]);

const hovered = ref(false);
const actionsMenuLabel = 'Ações da conversa';
const openActionsMenu = event => {
  const { right, bottom } = event.currentTarget.getBoundingClientRect();
  emit(
    'contextmenu',
    new MouseEvent('contextmenu', {
      clientX: right,
      clientY: bottom,
      cancelable: true,
    })
  );
};
const { uiSettings } = useUISettings();
const compactContacts = computed(
  () => uiSettings.value?.conversation_list_compact === true
);
const detailsExpanded = ref(false);
const showDetails = computed(
  () => !compactContacts.value || detailsExpanded.value
);

watch(compactContacts, () => {
  detailsExpanded.value = false;
});

const unreadCount = computed(() => props.chat.unread_count);
const hasUnread = computed(() => unreadCount.value > 0);
const lastMessageInChat = computed(() => getLastMessage(props.chat));
const getDraft = useMapGetter('draftMessages/get');
const draftLabels = { REPLY: 'Rascunho:', NOTE: 'Rascunho privado:' };
const draftPreview = computed(() => {
  return ['REPLY', 'NOTE']
    .map(mode => {
      const content = getDraft.value?.(`draft-${props.chat.id}-${mode}`) || '';
      if (!content.trim()) return null;
      const text = new MessageFormatter(content).plainText.trim();
      return text ? { text, label: draftLabels[mode] } : null;
    })
    .find(Boolean);
});
const contactAvatarIcon = computed(() => {
  const contactName = props.currentContact.name?.trim() || '';
  const isPhoneNumber = /^\+?[\d\s().-]{7,}$/.test(contactName);
  return !props.currentContact.thumbnail && isPhoneNumber
    ? 'i-lucide-user'
    : null;
});

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const showMetaSection = computed(() => {
  return props.showInboxName || (props.showAssignee && props.assignee.name);
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);
const hasActiveSla = computed(
  () =>
    Boolean(hasSlaPolicyId.value && props.chat?.applied_sla?.id) &&
    !props.currentContact?.blocked
);
const isAgentBotAssignee = computed(
  () =>
    props.chat?.meta?.assignee_type === 'AgentBot' ||
    Boolean(props.chat?.assignee_agent_bot_id)
);
const aiActiveLabel = 'IA ativa';
const snoozedUntil = computed(
  () => props.chat?.snoozed_until || props.chat?.snoozedUntil
);
const scheduledAtLabel = computed(() =>
  scheduledSnoozeDateTime(snoozedUntil.value)
);

const showLabelsSection = computed(() => {
  return (
    props.chat.labels?.length > 0 ||
    hasActiveSla.value ||
    Boolean(scheduledAtLabel.value)
  );
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.inbox.id);
  } else {
    emit('deSelectConversation', props.chat.id, props.inbox.id);
  }
};

const selectedModel = computed({
  get: () => props.selected,
  set: value => onSelectConversation(value),
});

watch(
  () => props.chat.id,
  () => {
    hovered.value = false;
    detailsExpanded.value = false;
  }
);
</script>

<template>
  <div
    data-testid="conversation-card"
    :aria-current="isActiveChat ? 'page' : undefined"
    class="conversation group relative mx-2 my-1 flex w-auto max-w-full flex-grow-0 flex-shrink-0 cursor-pointer items-start gap-3 rounded-2xl border transition-[background-color,border-color,box-shadow] duration-150 hover:z-[1]"
    :class="{
      'active z-[1] border-n-weak bg-n-solid-1 shadow-[0_3px_10px_rgba(15,23,42,0.08)]':
        isActiveChat,
      'selected border-n-brand/30 bg-n-alpha-2': selected && !isActiveChat,
      'border-transparent hover:border-n-weak hover:bg-n-alpha-1':
        !isActiveChat && !selected,
      'px-2': compact,
      'px-3': !compact,
      'py-2': compactContacts && !detailsExpanded,
      'py-3': !compactContacts || detailsExpanded,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div
      class="relative self-start shrink-0"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="36"
        :status="currentContact.availability_status"
        :icon-name="contactAvatarIcon"
        hide-offline-status
        rounded-full
        :class="
          isActiveChat &&
          'rounded-full ring-2 ring-n-brand/40 ring-offset-2 ring-offset-n-solid-1'
        "
      >
        <template v-if="showInboxName && inbox.id" #badge>
          <span
            class="absolute z-20 flex items-center justify-center rounded-full bg-n-solid-1 border border-n-slate-3 size-4 -bottom-1 -right-1"
          >
            <ChannelIcon
              :inbox="inbox"
              colored
              use-brand-icon
              class="size-3.5"
            />
          </span>
        </template>
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <Checkbox v-model="selectedModel" />
          </label>
        </template>
      </Avatar>
    </div>
    <div class="min-w-0 flex-1">
      <div class="mb-1 flex min-w-0 items-center gap-2">
        <h4
          class="conversation--user m-0 min-w-0 flex-1 truncate text-sm leading-5 text-n-slate-12"
          :class="hasUnread ? 'font-bold' : 'font-semibold'"
        >
          {{ currentContact.name }}
        </h4>
        <span
          v-if="
            isAgentBotAssignee &&
            !(showDetails && showAssignee && assignee.name)
          "
          v-tooltip.top="aiActiveLabel"
          :aria-label="aiActiveLabel"
          role="img"
          class="inline-flex size-4 shrink-0 items-center justify-center text-n-teal-11"
        >
          <Icon icon="i-lucide-bot" class="size-3.5" />
        </span>
        <CardPriorityIcon :priority="chat.priority" class="shrink-0 !size-4" />
        <span
          v-if="isPinned"
          v-tooltip.top="$t('CONVERSATION.CARD_CONTEXT_MENU.PINNED')"
          data-testid="pinned-conversation-badge"
          class="inline-flex shrink-0 items-center gap-1 text-[0.625rem] font-medium text-[#85858f]"
        >
          <Icon icon="i-hugeicons-pin-fill" class="size-3 !text-[#85858f]" />
          <span class="sr-only">{{
            $t('CONVERSATION.CARD_CONTEXT_MENU.PINNED_BADGE')
          }}</span>
        </span>
        <div class="flex h-5 shrink-0 items-center gap-1.5">
          <span
            class="inline-flex h-5 items-center text-[0.625rem] leading-none tabular-nums text-n-slate-11"
          >
            <TimeAgo
              calendar
              :last-activity-timestamp="chat.timestamp"
              :created-at-timestamp="chat.created_at"
              :conversation-id="chat.id"
            />
          </span>
          <UnreadBadge v-if="hasUnread" :count="unreadCount" />
        </div>
        <Button
          data-testid="conversation-actions"
          :aria-label="actionsMenuLabel"
          :title="actionsMenuLabel"
          aria-haspopup="menu"
          :aria-expanded="actionsMenuOpen"
          icon="i-lucide-chevron-down"
          variant="ghost"
          color="slate"
          size="xs"
          class="conversation-actions shrink-0 !size-6 !p-0 !rounded-full"
          :class="{ 'conversation-actions--open': actionsMenuOpen }"
          @click.stop="openActionsMenu"
        />
        <Button
          v-if="compactContacts && (showMetaSection || showLabelsSection)"
          data-testid="toggle-contact-details"
          variant="link"
          size="xs"
          class="shrink-0 !p-0"
          :label="detailsExpanded ? 'Ver menos' : 'Ver mais'"
          :aria-expanded="detailsExpanded"
          :aria-label="`${detailsExpanded ? 'Ver menos' : 'Ver mais'} informações de ${currentContact.name}`"
          @click.stop="detailsExpanded = !detailsExpanded"
        />
      </div>
      <p
        v-if="draftPreview"
        data-testid="conversation-draft"
        class="my-0 flex h-5 min-w-0 items-center gap-1 text-[0.8125rem] leading-5"
      >
        <span class="shrink-0 font-medium text-n-ruby-11">
          {{ draftPreview.label }}{{ ' ' }}
        </span>
        <span class="truncate text-n-slate-11">{{ draftPreview.text }}</span>
      </p>
      <VoiceCallStatus
        v-else-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        class="my-0 h-5 min-w-0 flex-1 text-[0.8125rem] leading-5"
        :class="messagePreviewClass"
      />
      <p
        v-else
        key="no-messages"
        class="my-0 h-5 min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[0.8125rem] leading-5 text-n-slate-11"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        v-if="showDetails && showMetaSection"
        class="mt-1.5 flex min-w-0 items-center gap-2 text-n-slate-10"
      >
        <InboxName
          v-if="showInboxName"
          :inbox="inbox"
          :show-icon="false"
          class="min-w-0 flex-1 opacity-80 [&_span]:text-[0.6875rem]"
        />
        <div
          class="flex min-w-0 max-w-[45%] items-center gap-1.5"
          :class="{
            'flex-1 justify-between': !showInboxName,
          }"
        >
          <span
            v-if="showAssignee && assignee.name"
            v-tooltip.top="isAgentBotAssignee ? aiActiveLabel : assignee.name"
            :aria-label="
              isAgentBotAssignee
                ? `${assignee.name}: ${aiActiveLabel}`
                : assignee.name
            "
            class="text-[0.6875rem] font-normal leading-4 py-0.5 px-0 inline-flex items-center gap-1 truncate"
            :class="isAgentBotAssignee ? 'text-n-teal-11' : 'text-n-slate-11'"
          >
            <Icon
              :icon="
                isAgentBotAssignee ? 'i-lucide-bot' : 'i-lucide-user-round'
              "
              class="size-3 flex-shrink-0"
            />
            <span class="truncate">{{ assignee.name }}</span>
          </span>
        </div>
      </div>
      <CardLabels
        v-if="showDetails && showLabelsSection"
        color-border
        :max-visible-labels="3"
        :labels="chat.labels || []"
        class="mb-0 mt-1.5"
      >
        <template v-if="scheduledAtLabel || hasActiveSla" #before>
          <SnoozeBadge
            v-if="scheduledAtLabel"
            :snoozed-until="snoozedUntil"
            class="ltr:mr-1 rtl:ml-1"
          />
          <SLACardLabel
            v-if="hasActiveSla"
            :chat="chat"
            class="ltr:mr-1 rtl:ml-1"
          />
        </template>
      </CardLabels>
    </div>
  </div>
</template>

<style scoped>
.conversation-actions {
  overflow: hidden;
  transition:
    width 150ms ease,
    margin-inline-start 150ms ease,
    opacity 150ms ease;
}

@media (hover: hover) and (pointer: fine) {
  .conversation-actions {
    width: 0 !important;
    min-width: 0 !important;
    margin-inline-start: -0.5rem;
    opacity: 0;
    pointer-events: none;
  }

  .conversation:hover .conversation-actions,
  .conversation:focus-within .conversation-actions,
  .conversation-actions--open {
    width: 1.5rem !important;
    margin-inline-start: 0;
    opacity: 1;
    pointer-events: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .conversation-actions {
    transition: none;
  }
}
</style>
